/**
 * New Ticket Form Component
 *
 * Form for creating new support tickets.
 * Supports pre-filling for reflection-linked issues.
 * Includes field-level validation with visual error feedback.
 */

import { useState } from "react";
import { Send, Loader2 } from "lucide-react";
import { Button } from "../ui/button";
import { cn } from "@/lib/utils";
import { useCreateTicket } from "../../hooks/useSupport";
import type {
  SupportTicketCategory,
  SupportTicketDetail,
} from "../../types/support";

interface FieldErrors {
  subject?: string;
  description?: string;
}

interface NewTicketFormProps {
  reflectionId?: string;
  onSuccess?: (ticket: SupportTicketDetail) => void;
  onCancel?: () => void;
}

export function NewTicketForm({
  reflectionId,
  onSuccess,
  onCancel,
}: NewTicketFormProps) {
  const [subject, setSubject] = useState("");
  const [description, setDescription] = useState(
    reflectionId
      ? `I'm having an issue with a reflection (ID: ${reflectionId}).\n\n`
      : ""
  );
  const [category, setCategory] = useState<SupportTicketCategory>(
    reflectionId ? "technical" : "other"
  );
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const createTicket = useCreateTicket();

  const validateForm = (): boolean => {
    const errors: FieldErrors = {};

    if (!subject.trim()) {
      errors.subject = "Subject is required";
    }

    if (!description.trim()) {
      errors.description = "Description is required";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateForm()) {
      return;
    }

    try {
      const ticket = await createTicket.mutateAsync({
        subject: subject.trim(),
        description: description.trim(),
        category,
        reflection_id: reflectionId,
      });
      onSuccess?.(ticket);
    } catch (err) {
      setError("Failed to create ticket. Please try again.");
      console.error("Failed to create ticket:", err);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
          {error}
        </div>
      )}

      <div>
        <label
          htmlFor="category"
          className="block text-sm font-medium text-foreground mb-1"
        >
          Category
        </label>
        <select
          id="category"
          value={category}
          onChange={(e) => setCategory(e.target.value as SupportTicketCategory)}
          className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="billing">Billing</option>
          <option value="technical">Technical Issue</option>
          <option value="account">Account</option>
          <option value="feedback">Feedback</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div>
        <label
          htmlFor="subject"
          className="block text-sm font-medium text-foreground mb-1"
        >
          Subject *
        </label>
        <input
          id="subject"
          type="text"
          value={subject}
          onChange={(e) => {
            setSubject(e.target.value);
            if (fieldErrors.subject) {
              setFieldErrors((prev) => ({ ...prev, subject: undefined }));
            }
          }}
          placeholder="Brief description of your issue"
          maxLength={255}
          className={cn(
            "w-full px-3 py-2 border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2",
            fieldErrors.subject
              ? "border-red-500 focus:ring-red-500"
              : "border-border focus:ring-primary"
          )}
        />
        {fieldErrors.subject && (
          <p className="mt-1 text-xs text-red-500">{fieldErrors.subject}</p>
        )}
      </div>

      <div>
        <label
          htmlFor="description"
          className="block text-sm font-medium text-foreground mb-1"
        >
          Description *
        </label>
        <textarea
          id="description"
          value={description}
          onChange={(e) => {
            setDescription(e.target.value);
            if (fieldErrors.description) {
              setFieldErrors((prev) => ({ ...prev, description: undefined }));
            }
          }}
          placeholder="Please describe your issue in detail..."
          rows={6}
          maxLength={10000}
          className={cn(
            "w-full px-3 py-2 border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 resize-none",
            fieldErrors.description
              ? "border-red-500 focus:ring-red-500"
              : "border-border focus:ring-primary"
          )}
        />
        {fieldErrors.description ? (
          <p className="mt-1 text-xs text-red-500">{fieldErrors.description}</p>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">
            {description.length}/10000 characters
          </p>
        )}
      </div>

      <div className="flex justify-end gap-3 pt-2">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={createTicket.isPending}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={createTicket.isPending}>
          {createTicket.isPending ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Creating...
            </>
          ) : (
            <>
              <Send className="w-4 h-4 mr-2" />
              Submit Ticket
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
